/**
 * Infrastructure markers — fed by `/api/infra/sites` (REST snapshot) overlaid with
 * live heartbeat state from useInfraStore.
 *
 * Subscribed alongside the other GL layers in MapCanvas `attachCore` so it
 * re-attaches on `style.load` after a basemap swap. Reads `InfraSite[]`
 * from the TanStack Query cache (`['infra','sites']`) — no parameter on
 * the subscribe call.
 *
 * Mockup parity: this layer is ONLY visible in infra-ctx. Drones / OI / Surv
 * each wipe foreign markers on entry (mockup `selectContext` L4956-4973 /
 * L5046 / L5126). We mirror that by gating `visibility` on the live `ctx`
 * state — the layer file owns its own gate so a late layer-add can't race
 * the MapCanvas visibility effect.
 */
import mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, Map } from 'mapbox-gl';
import { queryClient } from '@/lib/query';
import type { InfraSite } from '@/lib/api/types';
import { useInfraStore } from '@/lib/store/infra';
import { useUiStore } from '@/lib/store/ui';

const SRC = 'src-infra';
const LAYER = 'lyr-infra';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function buildInfraPopupHtml(props: Record<string, unknown>): string {
  const id = String(props.id ?? '—');
  const typeLabel = props.type === 'M' ? 'Mobile' : 'Fixed';
  const addrShort = String(props.address ?? '')
    .split(',')
    .slice(0, 2)
    .join(',')
    .trim();
  const backhaul = String(props.backhaul ?? '');
  const latencyMs = props.latency_ms as number | null | undefined;
  const latencyOk = props.latency_state === 'ok';
  const online = Number(props.online_count ?? 0);
  const total = Number(props.total_count ?? 0);
  const bhBg = latencyOk ? '#f0fdf4' : '#fefce8';
  const bhFg = latencyOk ? '#166534' : '#92400e';
  const latencyChip =
    latencyMs != null
      ? `<span style="background:#f0fdf4;color:#166534;padding:1px 7px;border-radius:3px;font-weight:600">` +
        `${escapeHtml(String(Math.round(latencyMs)))} ms</span>`
      : '';
  return (
    `<div style="font-family:Inter,sans-serif;min-width:190px">` +
    `<div style="font-weight:700;font-size:0.846em;margin-bottom:3px">${escapeHtml(id)} ` +
    `<span style="font-weight:400;font-size:0.769em;color:#888">(${typeLabel})</span></div>` +
    (addrShort
      ? `<div style="font-size:0.769em;color:#666;margin-bottom:5px">${escapeHtml(addrShort)}</div>`
      : '') +
    `<div style="font-size:0.769em;display:flex;gap:5px;flex-wrap:wrap">` +
    (backhaul
      ? `<span style="background:${bhBg};color:${bhFg};padding:1px 7px;border-radius:3px;` +
        `font-weight:600">📡 ${escapeHtml(backhaul)}</span>`
      : '') +
    latencyChip +
    `</div>` +
    `<div style="font-size:0.769em;color:#555;margin-top:5px">` +
    `${online}/${total} devices online</div>` +
    `</div>`
  );
}

function visibilityForCtx(ctx: string): 'visible' | 'none' {
  return ctx === 'infra' ? 'visible' : 'none';
}

export function ensureInfraLayer(map: Map) {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    const ctx = useUiStore.getState().activeContext;
    map.addLayer({
      id: LAYER,
      type: 'circle',
      source: SRC,
      layout: { visibility: visibilityForCtx(ctx) },
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 10],
        'circle-color': [
          'match',
          ['get', 'status'],
          'active',
          '#22c55e',
          'degraded',
          '#f59e0b',
          'inactive',
          '#c41a1a',
          '#6b7280',
        ],
        // Mockup `drawInfraMarkers` L11903 uses a darker, status-matching
        // border (`borderColor` derived from latencyState). We mirror that
        // for redundant visual status — the white stroke we previously used
        // discarded that signal.
        'circle-stroke-width': 2,
        'circle-stroke-color': [
          'match',
          ['get', 'status'],
          'active',
          '#1d7a40',
          'degraded',
          '#b45309',
          'inactive',
          '#7f1d1d',
          '#374151',
        ],
        'circle-opacity': 0.9,
      },
    });
  }
}

export function syncInfraLayer(map: Map, sites: InfraSite[]) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const infra = useInfraStore.getState();
  const live = infra.byId;
  // Status / type chips (InfraList header + the infra ContextToggles row)
  // filter the MAP as well as the list — same predicate as InfraList so the
  // two always show the same set of sites. Deliberately keyed off the REST
  // `status` the list filters on, not the heartbeat-overridden status used
  // for the marker colour below, so a chip never hides a row that stays
  // listed.
  const features = sites
    .filter(
      (s) =>
        infra.filterStatus[s.status as 'active' | 'degraded' | 'inactive'] &&
        infra.filterType[s.type],
    )
    .map((s) => {
      const hb = live[s.site_id];
      const backhaul = [
        s.backhaul_primary,
        s.backhaul_secondary && s.backhaul_secondary !== 'n/a' ? s.backhaul_secondary : '',
      ]
        .filter(Boolean)
        .join(' + ');
      const onlineCount = s.components.filter((c) => c.online).length;
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [s.lng, s.lat] },
        properties: {
          id: s.site_id,
          status: hb?.status ?? s.status,
          type: s.type,
          address: s.address,
          backhaul,
          latency_ms: hb?.latency_ms ?? null,
          latency_state: hb?.latency_state ?? null,
          online_count: onlineCount,
          total_count: s.components.length,
        },
      };
    });
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Subscribe the infra GL layer. Reads `InfraSite[]` straight from the
 * TanStack Query cache (`['infra','sites']`) so the function is
 * parameter-less and can live in MapCanvas `attachCore` alongside every
 * other layer — this is what survives a basemap swap (`m.setStyle()` →
 * `style.load` → `attachCore` re-runs). The previous "standalone
 * useEffect with `infraSites` dep" pattern didn't re-run on basemap
 * change and the layer stayed gone until context flip.
 */
export function subscribeInfraLayer(map: Map): () => void {
  ensureInfraLayer(map);
  const getSites = (): InfraSite[] =>
    queryClient.getQueryData<InfraSite[]>(['infra', 'sites']) ?? [];

  let rafId: number | null = null;
  // No `idle` retry. Each `setData` causes Mapbox to repaint, which
  // triggers another `idle` — wiring sync to idle would recurse at the
  // browser's frame rate. Source-readiness is handled by `attachCore`
  // running on `style.load`; every other retrigger comes from store
  // subscriptions below.
  syncInfraLayer(map, getSites());
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncInfraLayer(map, getSites());
    });
  };

  const unsubLive = useInfraStore.subscribe((s) => s.byId, schedule);
  const unsubFilters = useInfraStore.subscribe(
    (s) => `${JSON.stringify(s.filterStatus)}|${JSON.stringify(s.filterType)}`,
    schedule,
  );
  // Re-sync when the REST sites query lands or refetches — replaces the
  // old "useEffect with infraSites dep" path now that subscribe is in
  // `attachCore`.
  const qUnsub = queryClient.getQueryCache().subscribe((event) => {
    const k = event.query.queryKey;
    if (k[0] === 'infra' && k[1] === 'sites') schedule();
  });
  const unsubCtx = useUiStore.subscribe(
    (s) => s.activeContext,
    (ctx) => {
      if (map.getLayer(LAYER)) {
        map.setLayoutProperty(LAYER, 'visibility', visibilityForCtx(ctx));
      }
    },
  );

  // Mockup `circle.bindTooltip` (L11907) + `selectInfraSite` click (L11924) —
  // one shared mapboxgl.Popup for the hover/click detail card; clicking the
  // marker ALSO selects the site so the LP detail panel opens (mockup L11924).
  const popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '230px',
    offset: 12,
  });
  const onClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    const f = e.features?.[0];
    if (!f) return;
    popup
      .setLngLat(e.lngLat)
      .setHTML(buildInfraPopupHtml((f.properties ?? {}) as Record<string, unknown>))
      .addTo(map);
    const id = (f.properties as { id?: string } | null)?.id;
    if (id) useInfraStore.getState().select(id);
  };
  const onMouseEnter = () => {
    map.getCanvas().style.cursor = 'pointer';
  };
  const onMouseLeave = () => {
    map.getCanvas().style.cursor = '';
  };
  map.on('click', LAYER, onClick);
  map.on('mouseenter', LAYER, onMouseEnter);
  map.on('mouseleave', LAYER, onMouseLeave);

  // Mockup `selectInfraSite` (L11428-11430) — when a site is selected (either
  // via marker click or via LP list click), pan the map to centre on that
  // site at zoom 13. Skipped on the very first store hydration when the
  // selectedId was already set from localStorage.
  let fitId: number | null = null;
  const focusSite = (siteId: string | null) => {
    if (!siteId) return;
    const site = getSites().find((s) => s.site_id === siteId);
    if (!site) return;
    if (!map.isStyleLoaded()) {
      map.once('idle', () => focusSite(siteId));
      return;
    }
    map.easeTo({ center: [site.lng, site.lat], zoom: Math.max(map.getZoom(), 13), duration: 500 });
  };
  const unsubSel = useInfraStore.subscribe(
    (s) => s.selectedId,
    (selId) => {
      if (fitId !== null) clearTimeout(fitId);
      fitId = window.setTimeout(() => {
        fitId = null;
        focusSite(selId);
      }, 80);
    },
  );

  return () => {
    unsubLive();
    unsubFilters();
    unsubCtx();
    unsubSel();
    qUnsub();
    if (fitId !== null) clearTimeout(fitId);
    map.off('click', LAYER, onClick);
    map.off('mouseenter', LAYER, onMouseEnter);
    map.off('mouseleave', LAYER, onMouseLeave);
    popup.remove();
  };
}
